const VALID_TYPES = new Set(['T', 'rotatoria', 'semaforizzata']);
/** Geometric intersection classes used for the traffic-light outline (Semafix v2 §3). */
const GEOMETRY_TYPES = new Set(['T', 'rotatoria', 'cross', 'altro']);

/** Derive the geometric class of a node (independent of whether it is signalized). */
function geometryTypeOf(intersection) {
  if (GEOMETRY_TYPES.has(intersection.geometryType)) return intersection.geometryType;
  if (intersection.type === 'T' || intersection.type === 'rotatoria') return intersection.type;
  // A signalized node keeps its geometric class in `geometryType` when the
  // backend provides it; otherwise it is unknown ('altro').
  return 'altro';
}

export const DEFAULT_NODE_TYPE_FILTERS = Object.freeze({
  T: true,
  rotatoria: true,
  cross: true,
  altro: true,
});

function finite(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

export function normalizeCatalog(raw = {}) {
  const roads = (raw.roads || []).filter((road) => Array.isArray(road.co) && road.co.length > 1).map((road) => ({
    ...road,
    id: String(road.id),
    n: road.n || 'Strada senza nome',
    lanes: Math.max(1, finite(road.lanes, 1)),
    len: finite(road.len),
    speed: finite(road.speed),
    cap: Math.max(1, finite(road.cap, 1)),
    base: Math.max(0, finite(road.base)),
    flowSource: road.flowSource === 'observed' || road.monitored ? 'observed' : 'estimated',
    monitored: Boolean(road.monitored || road.flowSource === 'observed'),
  }));
  const roadById = new Map(roads.map((road) => [road.id, road]));

  const intersections = (raw.intersections || []).map((intersection) => ({
    ...intersection,
    id: String(intersection.id),
    lat: finite(intersection.lat),
    lng: finite(intersection.lng),
    type: VALID_TYPES.has(intersection.type) ? intersection.type : 'altro',
    // Semafix v2 §3: the traffic-light marker encodes two facts —
    //  - geometryType -> circle outline colour (geometric class)
    //  - satelliteAvailable -> coloured (available) vs grey (not) icon
    geometryType: geometryTypeOf(intersection),
    satelliteAvailable: Boolean(intersection.satelliteAvailable),
    arms: (intersection.arms || []).map((arm) => ({
      ...arm,
      roadId: String(arm.roadId || ''),
      road: arm.road || roadById.get(String(arm.roadId))?.n || 'Strada senza nome',
      lanes: Math.max(1, finite(arm.lanes, 1)),
      laneWidth: Math.max(0, finite(arm.laneWidth, 3.25)),
      cap: Math.max(1, finite(arm.cap, 1)),
      flow: Math.max(0, finite(arm.flow)),
      speed: finite(arm.speed),
      bearing: finite(arm.bearing),
    })),
    maneuversPreview: (intersection.maneuversPreview || []).map((maneuver) => ({
      ...maneuver,
      id: String(maneuver.id),
      lanes: Math.max(1, finite(maneuver.lanes, 1)),
      laneWidth: Math.max(0, finite(maneuver.laneWidth, 3.25)),
      capSatResult: Math.max(1, finite(maneuver.capSatResult, 1200)),
      latlngs: (maneuver.latlngs || []).map((point) => ({
        lat: finite(point.lat ?? point[0]),
        lng: finite(point.lng ?? point[1]),
      })),
    })),
  }));
  const signals = (raw.signals || []).map((signal) => ({
    ...signal,
    id: String(signal.id),
    intersectionId: String(signal.intersectionId),
    green: finite(signal.green, 30),
    yellow: finite(signal.yellow, 3),
    red: finite(signal.red, 30),
    optimizedPlan: signal.optimizedPlan || null,
  }));
  const crossings = (raw.crossings || []).map((crossing) => ({
    ...crossing,
    id: String(crossing.id),
    signalId: crossing.signalId == null ? null : String(crossing.signalId),
  }));

  return {
    roads,
    roadById,
    intersections,
    signals,
    signalByIntersection: new Map(signals.map((signal) => [signal.intersectionId, signal])),
    crossings,
  };
}

export function maneuverRouteLabel(maneuver) {
  if (maneuver?.fromRoad && maneuver?.toRoad) return `${maneuver.fromRoad} → ${maneuver.toRoad}`;
  return maneuver?.title || '';
}

export function movementTypeLabel(type) {
  if (type === 'left') return 'Svolta sinistra';
  if (type === 'right') return 'Svolta destra';
  return 'Dritto';
}

export function directionLabel(bearing = 0) {
  const normalized = (Number(bearing) + 360) % 360;
  if (normalized < 22.5 || normalized >= 337.5) return 'Nord';
  if (normalized < 67.5) return 'Nord-est';
  if (normalized < 112.5) return 'Est';
  if (normalized < 157.5) return 'Sud-est';
  if (normalized < 202.5) return 'Sud';
  if (normalized < 247.5) return 'Sud-ovest';
  if (normalized < 292.5) return 'Ovest';
  return 'Nord-ovest';
}

export function intersectionTypeLabel(type) {
  if (type === 'T') return 'Intersezione a T';
  if (type === 'rotatoria') return 'Rotatoria';
  if (type === 'semaforizzata') return 'Intersezione semaforizzata';
  return 'Intersezione';
}
